/**
 * Beta classification — shared by the stock-detail key-stats grid and the
 * ticker popup so a number never means one thing in one place and something
 * else in another.
 *
 * Beta measures how far a stock has historically moved for a given move in
 * the broad market (S&P 500 = 1.00). It is a RISK reading, not a quality one:
 * a 0.4 beta is not "good" and a 2.5 is not "bad", they are different amounts
 * of exposure. So the palette is a cool→warm risk gradient rather than the
 * red/green the rest of the app uses for gains and losses — colouring a high
 * beta red would read as "this stock is down", which it does not mean.
 */

export type BetaTier = 'inverse' | 'defensive' | 'market' | 'aggressive' | 'volatile';

/** Boundaries. The 0.8–1.2 band around 1.00 is the conventional "moves with
 *  the market" window — tight enough that 1.35 reads as genuinely livelier,
 *  wide enough that 0.95 is not called defensive. */
export const BETA_DEFENSIVE_MAX = 0.8;
export const BETA_MARKET_MAX = 1.2;
export const BETA_AGGRESSIVE_MAX = 2;

export function betaTier(beta: number | null | undefined): BetaTier | null {
  if (beta == null || !Number.isFinite(beta)) return null;
  if (beta < 0) return 'inverse';
  if (beta < BETA_DEFENSIVE_MAX) return 'defensive';
  if (beta <= BETA_MARKET_MAX) return 'market';
  if (beta <= BETA_AGGRESSIVE_MAX) return 'aggressive';
  return 'volatile';
}

/**
 * Whether a beta is a reading at all. The API coerces a NULL tickers.beta to
 * 0 (rowToStock's `n()` helper) and no sync path ever stores a genuine 0
 * (fundamentalsSync writes NULLIF(beta, 0)), so 0 means "not available",
 * never "moves 0x the market". Callers render '—' / nothing for it rather
 * than a 0.00 that reads like a real number.
 */
export function hasBeta(beta: number | null | undefined): beta is number {
  return beta != null && Number.isFinite(beta) && beta !== 0;
}

/** Text colour per tier. Slate at the market band so an ordinary beta stays
 *  quiet and the eye is only pulled by the ones that are actually unusual. */
export const BETA_TIER_TEXT: Record<BetaTier, string> = {
  inverse:    'text-violet-300',
  defensive:  'text-sky-300',
  market:     'text-slate-300',
  aggressive: 'text-amber-300',
  volatile:   'text-orange-400',
};

/** Same scale as a bordered chip, for surfaces that want a badge. */
export const BETA_TIER_CHIP: Record<BetaTier, string> = {
  inverse:    'bg-violet-500/15 text-violet-300 border-violet-500/30',
  defensive:  'bg-sky-500/15 text-sky-300 border-sky-500/30',
  market:     'bg-slate-500/15 text-slate-300 border-slate-500/30',
  aggressive: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  volatile:   'bg-orange-500/20 text-orange-300 border-orange-500/40',
};

export const BETA_TIER_LABEL: Record<BetaTier, string> = {
  inverse:    'Inverse',
  defensive:  'Defensive',
  market:     'Market-like',
  aggressive: 'Aggressive',
  volatile:   'High volatility',
};

/**
 * Tooltip text: what beta is, then what THIS value says in plain terms.
 *
 * The multiplier sentence is the part people actually want — "3.23" means
 * nothing on its own, "moved about 3.2x as much as the market" does.
 */
export function formatBetaTooltip(beta: number | null | undefined): string {
  const tier = betaTier(beta);
  if (tier == null) return 'Beta — how much this stock moves relative to the S&P 500. Not available.';

  const b = beta as number;
  const shown = b.toFixed(2);
  const magnitude = Math.abs(b).toFixed(1);
  const head = `Beta ${shown} — ${BETA_TIER_LABEL[tier].toLowerCase()}.`;
  const base = 'Beta measures how far this stock has historically moved for a given move in the S&P 500 (market = 1.00).';

  const detail =
    tier === 'inverse'
      ? `A negative beta means it has tended to move AGAINST the market, roughly ${magnitude}x as far in the opposite direction.`
      : tier === 'defensive'
        ? `It has moved about ${magnitude}x as much as the market — smaller swings in both directions.`
        : tier === 'market'
          ? 'It has moved roughly in line with the market.'
          : `It has moved about ${magnitude}x as much as the market — larger swings in both directions, up and down.`;

  return `${head} ${base} ${detail}`;
}

/** Short suffix for compact surfaces that can't fit the full tooltip inline. */
export function formatBetaShort(beta: number | null | undefined): string | null {
  if (beta == null || !Number.isFinite(beta)) return null;
  return beta.toFixed(2);
}
